define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/query",
	"dojo/store/Memory",
	"dijit/form/Button",
	"dijit/registry",
	"csi/_base/underscore",
	"csi/comm",
	"csi/data/_base",
	"csi/data/ExtendedDataPoint",
	"csi/core/ExtendedPath",
	"csi/data/GroupingStore",
	"csi/heading/AttributeController",
	"csi/html",
	"csi/locale",
	"csi/prefs/BrowseAttributesStore",
	"csi/reflection",
	"csi/store/common",
	"csi/utility/stringUtility",
	"csi/widget/Dialog",
	"csi/widget/TemplatedFormWidget",
	"csi/xml",
	"dojo/text!../templates/queryFilterRow.html",
	"dojo/text!../templates/queryFilterDialog.html",

	"csi/widget/CheckBox",
	"dijit/form/DateTextBox",
	"csi/widget/Select",
	"csi/widget/ValidationTextBox"
], function(array, declare, lang, domClass, construct, query, Memory, Button, registry, _, comm, csiData, ExtendedDataPoint, ExtendedPath, GroupingStore,
	AttributeController, html, locale, BrowseAttributesStore, reflection, {DISABLED_PROPERTY}, {stringSubstitute}, Dialog, TemplatedFormWidget, xml, templateRow, templateDialog) {

	function _showDOMNode(/*DOMNode*/node, /*Boolean*/bShow) {
		domClass.toggle(node, "nodisplay", !bShow);
	}

	function _showWidget(/*_WidgetBase*/w, /*Boolean*/bShow) {
		// disabled hidden widgets to remove them from the validation
		w.set("disabled", !bShow);
		_showDOMNode(w.domNode, bShow);
	}

	function isBooleanDataPoint(/*ExtendedDataPoint*/dp) {
		var dataType = dp.getDataType();
		return (dataType == xml.ATTR_BOOLEAN);
	}

	function isDateDataPoint(/*ExtendedDataPoint*/dp) {
		var dataType = dp.getDataType();
		return (dataType == xml.ATTR_TIME);
	}

	function isTimeDataPoint(/*ExtendedDataPoint*/dp) {
		return false;
	}

	function isEnumDataPoint(/*ExtendedDataPoint*/dp) {
		var dataType = dp.getDataType();
		return (dataType == xml.ATTR_ENUM);
	}

	return declare(AttributeController, {
		_storeAttributes: null,
		_storeRelativeAttributes: null,

		/** @property {string} andOr with the value either "QueryFilter_BooleanOperation:AND" or "QueryFilter_BooleanOperation:OR" */
		andOr: "QueryFilter_BooleanOperation:AND",

		//--------------------------------------------------------------------------------
		//			Extension Points
		//--------------------------------------------------------------------------------

		getAttributesStore: function() {
			// summary: return store with allowed attributes (required)
			return null;
		},

		getRelativeAttributesStore: function() {
			// summary: return store with relative attributes (optional)
			return null;
		},


		//--------------------------------------------------------------------------------
		//			Controller interface
		//--------------------------------------------------------------------------------

		preProcess: function() {
			// open dialog to create new preferences
			var self = this;
			var dlg = this._dialog = new Dialog({
				"class": "csi-edit-query-dialog",
				dialogActions: true,
				// NOTE: setting content this way forces us to use _nlsEscaped in template for all substitutions
				content: stringSubstitute(templateDialog),
				title: locale.resource("EditQuery"),
				processValues: true,
				destroyOnHide: true,
				onStartup: lang.hitch(this, this._onLoad),
				onCancel: lang.hitch(this, this._onCancel),
				onDestroy: lang.hitch(this, this._onDestroy),
				onSave: lang.hitch(this, this._onSave),
				onRenderDialogActions: function(dom) {
					new Button({
						"class": html.CSS_BUTTON_SECONDARY,
						label: _.escape(locale.resource("EditQueryAddFilter")),
						onClick: function() {self.onAddFilter();}
					}).placeAt(dom, "first");
				}
			});

			var store;
			if (store = this.getAttributesStore()) {
				this._storeAttributes = this._wrapAttributeStore(store);
			}

			if (store = this.getRelativeAttributesStore()) {
				this._storeRelativeAttributes = this._wrapAttributeStore(store);
			}

			var cache = this.getCache();
			var node = this.getNode();

			if (cache && node) {
				const filterType = this.getAction().heading?.m_id ?? "QueryFilter";
				const queryFilter = cache.get(node[filterType]);
				if (queryFilter) {
					this.andOr = queryFilter.Operation;
				}
			}

			registry.byId("selectAndOr").set("value", xml.enumGetText(this.andOr) === "OR" ? "1" : "0");

			dlg.startup();
			dlg.show();
			this.suspendProcess();
		},

		getStringParameters: function() {
			return [this.heading.getLabel()];
		},

		toWriter: function() {
			var node = this.getNode();
			var attrId = this.heading.m_id;
			var w = this.m_writer;

			// delete the old filter data first
			w.deleteNode(node[attrId]);

			// get the array of filters and create new nodes for all filters
			var values = this._dialog.get("value");
			var filtersData = values.filterRow;
			if (filtersData) {
				if (!_.isArray(filtersData))
					filtersData = [filtersData];

				// for each filter create a separate node
				var filterUrls = array.map(filtersData, function(filterData) {
					var dp = ExtendedDataPoint.parse(filterData.attribute);

					var url = comm.createUrl();
					var cn = w.createNode(url, "QueryFilter");
					cn.changeAttribute("Operation", xml.ATTR_ENUM, filterData.operation)
						.changeAttribute("Relative", xml.ATTR_BOOLEAN, filterData.relative);

					// record the value from different widgets depending on the data point type
					var value = filterData.value1;
					if (isBooleanDataPoint(dp))
						value = filterData.booleanValue1;
					else if (isDateDataPoint(dp))
						value = filterData.dateValue1 ? filterData.dateValue1.valueOf() : 0;
					else if (isEnumDataPoint(dp))
						value = filterData.enumValue1;

					cn.changeAttribute("Value", xml.ATTR_STRING, value);

					// record data points
					var dataPoints = {attribute: filterData.attribute};
					if (filterData.value1Attribute)
						dataPoints.value = filterData.value1Attribute;

					w.changeNode(url + "?Path=Child:DataPointContainer")
						.changeAttribute("DataPointMap", xml.ATTR_STRINGMAP, dataPoints);
					return url;
				}, this);

				// create QueryFilter_BooleanExpression with the AND operation
				var url = comm.createUrl();
				w.createNode(url, "QueryFilter_BooleanExpression", values.editFilterName)
					.changeAttribute("Operation", xml.ATTR_ENUM, this.andOr)
					.changeAttribute("Operands", xml.ATTR_REFVECTOR, filterUrls);

				// connect QueryFilter_BooleanExpression to the node
				w.changeNode(this.m_urlNode)
					.changeAttribute(attrId, xml.ATTR_REF, url);
			}
		},

		postProcess: function(/*Boolean*/bStatus, /*Boolean*/bRepeat) {
			if (this._dialog) {
				if (bStatus === false) {
					bRepeat = true;
					this._dialog.resumeAfterSave();
				} else {
					this._dialog.hide();
				}
			}

			this.inherited(arguments, [bStatus, bRepeat]);
		},


		//--------------------------------------------------------------------------------
		//			Dialog Event handlers
		//--------------------------------------------------------------------------------

		_onSave: function() {
			if (this.isSuspended() && this._dialog.validate()) {
				this.resumeProcess();
				this.process();
			}
		},

		_onCancel: function() {
			if (this.isSuspended()) {
				this.resumeProcess();
				this.postProcess();
			}
		},

		_onDestroy: function() {
			this._dialog = null;
		},

		onAddFilter: function() {
			var w = this._addFilterRowWidget();
			this._showHideWidgets(w);
			w.attributeNode && w.attributeNode.focus();
		},

		onRemoveFilter: function(/*Event*/e) {
			// get the parent widget and delete it
			var w = registry.getEnclosingWidget(e.target);
			w = w.getParent();
			w.destroyRecursive();
		},

		onChangeRelative: function(/*QueryFilterRow*/w, /*Boolean*/relative) {
			// adjust a visibility of value nodes accordingly
			var strDataPoint = w.attributeNode.get("value");
			this._onChangeSelection(w, strDataPoint, relative);
		},

		onChangeAttribute: function(/*QueryFilterRow*/w, /*String*/strDataPoint) {
			var relative = w.relativeNode.get("value");
			var dp = this._onChangeSelection(w, strDataPoint, relative);

			// set up a store for enumerations drop-down if the attribute is an enumeration
			if (isEnumDataPoint(dp)) {
				var ra = dp.getReflectionAttribute();
				var val = ra.getValue();
				var index = val.indexOf(':');
				if (index >= 0)
					val = val.substring(0, index);

				var enumList = reflection.getEnumList(val);
				var store = this._getEnumerationStore(enumList.getEnums());

				currentValue = w.enumValue1Node.get("value");
				w.enumValue1Node.set("store", store);
				w.enumValue1Node.set("value", currentValue);
			}
		},


		//--------------------------------------------------------------------------------
		//			Private functions
		//--------------------------------------------------------------------------------

		_onLoad: function() {
			// hide relative column if no store
			if (!this._storeRelativeAttributes) {
				domClass.add(this._dialog.domNode, "csi-edit-query-filter-no-rel");
			}

			// create and fill out filter widgets
			this._initializeFilterRowWidgets();

			registry.byId("selectAndOr").onChange = (value) => {
				this.andOr = xml.enumCreate("QueryFilter_BooleanOperation", (value == "0" ? "AND" : "OR"));
			};
		},

		_initializeFilterRowWidgets: function() {
			var cache = this.getCache();
			var node = this.getNode();

			var dialogValues = {filterRow: []};

			var boolExpr = cache.getNode(node[this.heading.m_id], true);
			if (boolExpr)
				dialogValues.editFilterName = boolExpr.$Name;

			var filters = cache.getChildren(boolExpr, "Operands", true);
			array.forEach(filters, function(filter) {
				var w = this._addFilterRowWidget();
				var dataPointContainer = cache.getNode(filter.DataPointContainer, true);
				var dataPoints = dataPointContainer.DataPointMap;

				var value = {
					attribute: dataPoints.attribute,
					operation: filter.Operation,
					relative: filter.Relative,
					value1Attribute: dataPoints.value
				};

				var dp = ExtendedDataPoint.parse(dataPoints.attribute);

				if (isBooleanDataPoint(dp))
					value.booleanValue1 = xml.valueIsStringTrue(filter.Value);
				else if (isDateDataPoint(dp)) {
					var num = Number(filter.Value);
					value.dateValue1 = Boolean(num) ? new Date(num) : new Date();
				} else if (isEnumDataPoint(dp))
					value.enumValue1 = filter.Value;
				else
					value.value1 = filter.Value;

				// make sure all stores are set up correctly before we assign values to the dialog
				this.onChangeAttribute(w, value.attribute);

				dialogValues.filterRow.push(value);
			}, this);

			this._dialog.set("value", dialogValues);
		},

		_addFilterRowWidget: function() {
			var domRoot = this._dialog.domNode;
			var body = query(".csi-edit-query-dialog-filters tbody", domRoot)[0];

			var w = new TemplatedFormWidget({
				name: "filterRow",
				templateString: templateRow
			}, construct.create("tr", null, body));
			w.startup();

			w.attributeNode.set("store", this._storeAttributes);
			w.operationNode.setStore(this._getOperationStore());

			// setup relative attribute store
			if (this._storeRelativeAttributes) {
				w.value1AttributeNode.set("store", this._storeRelativeAttributes);
				this._connectEvent(w.relativeNode, "onChange", lang.hitch(this, this.onChangeRelative, w));
			}

			this._connectEvent(w.attributeNode, "onChange", lang.hitch(this, this.onChangeAttribute, w));
			this._connectEvent(w.removeFilterButton, "onClick", this.onRemoveFilter);

			return w;
		},

		_onChangeSelection: function(/*QueryFilterRow*/w, /*String*/strDataPoint, /*Boolean*/relative) {
			var dp = ExtendedDataPoint.parse(strDataPoint);

			// set up a store with available operations
			w.operationNode.setStore(this._getOperationStore(dp, relative), w.operationNode.get("value"));

			if (dp) {
				this._showHideWidgets(w, dp, relative);
			}

			return dp;
		},

		_getOperationStore: function(/*ExtendedDataPoint*/dp, /*Boolean*/relative) {
			var enumList = reflection.getEnumList("QueryFilter_ComparisonOperation");
			var enumValues;

			if (!dp)
				enumValues = enumList.getEnums();
			else {
				var type = dp.getDataType();

				enumValues = [];
				function addOp(op) {
					enumValues.push(enumList.getValue(op));
				}

				// summary: return store filled with possible operators
				if (type == xml.ATTR_BOOLEAN) {
					addOp("Equals");
				} else if (xml.attributeTypeIsContainer(type)) {
					addOp("Contains");
					addOp("NotContains");

					if (xml.attributeTypeGetBase(type) == xml.ATTR_STRING && !relative) {
						addOp("Matches");
						addOp("NotMatches");
					}
				} else if (dp) {
					var isNodeType = false;

					addOp("Equals");
					addOp("NotEquals");

					if (type == xml.ATTR_STRING && !isNodeType && !relative) {
						addOp("Matches");
						addOp("NotMatches");
					}

					if (type != xml.ATTR_ENUM && !isNodeType) {
						addOp("GreaterThan");
						addOp("GreaterThanOrEquals");
						addOp("LessThan");
						addOp("LessThanOrEqual");
					}
				}
			}

			return this._getEnumerationStore(enumValues);
		},

		_getEnumerationStore: function(/*String[]*/enumValues) {
			return new Memory({
				data: array.map(enumValues, function(v) {
					return {id: v, name: locale.getEnumName(v)};
				})
			});
		},

		_showHideWidgets: function(/*QueryFilterRow*/w, /*ExtendedDataPoint*/dp, /*Boolean*/relative) {
			// show/hide a correct editing widget according to the data point type
			if (relative) {
				dp = null;
			}

			var isBoolean = dp && isBooleanDataPoint(dp);
			var isDate = dp && isDateDataPoint(dp);
			var isTime = dp && isTimeDataPoint(dp);
			var isEnum = dp && isEnumDataPoint(dp);
			var isText = dp && !(isBoolean || isDate || isTime || isEnum);

			_showWidget(w.value1Node, isText);
			_showWidget(w.booleanValue1Node, isBoolean);
			_showWidget(w.dateValue1Node, isDate);
			_showWidget(w.enumValue1Node, isEnum);
			_showWidget(w.value1AttributeNode, !!relative);
		},

		_wrapAttributeStore: function(/*BrowseAttributesStore*/store) {
			// summary: wrap attribute store with grouping store
			var groupingStore = csiData.addDojoStoreAPI(new GroupingStore());

			// set default sorting
			store.options.sortSpec = store.options.sortSpec || [{attribute: "rcOriginal"}, {attribute: "isRelationship"}, {attribute: "text"}];

			groupingStore.cssProperty = "css";
			groupingStore.setOriginalStore(store);

			groupingStore.groupIdFunc = function(item) {
				return locale.type(item.rcOriginal.getType());
			};
			groupingStore.labelFunc = function(groupId) {
				return groupId;
			};
			groupingStore.adjustGroupItemFunc = function(item) {
				item[DISABLED_PROPERTY] = true;
				item.css = "csi-grouped-selection";
			};

			return groupingStore;
		},

		_createStoreFromType: function(type, paths) {
			var store = new BrowseAttributesStore(reflection.getClass(type));
			array.forEach(paths, function(obj) {
				var p = obj.m_path;
				var path = new ExtendedPath(type);

				if (p.indexOf("|") > 0) {
					// cannot use standard path, must resolve step by step and use target separated by |
					p = p.split("/");
					array.forEach(p, function(s) {
						s = s.split("|");
						path.addRelationStep(s[0], s[1]);
					});
				} else {
					path.addPath(p);
				}

				if (obj.hasOwnProperty("groupIdFunc") && typeof obj.groupIdFunc == "function") {
					path.groupIdFunc = obj.groupIdFunc;
				}

				store.addTopLevelItemsFromPath(path);
			});

			return store;
		}
	});
});